import { createFileRoute } from "@tanstack/react-router";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { Loader } from "@bfc-software/pallet";

import { formatClock } from "../system-controls/-data/shift-time";
import { CompletionHero } from "./-components/completion-hero";
import type { DashboardT } from "./-components/dashboard-t";
import { ExceptionsPanel } from "./-components/exceptions-panel";
import { PiecesPerHour } from "./-components/pieces-per-hour";
import { StatCards } from "./-components/stat-cards";
import { ZoneProgressCard } from "./-components/zone-progress";
import styles from "./-components/dashboard.module.css";
import { useDashboard } from "./-data/use-dashboard";

/**
 * The Outbound Dashboard: the shift as it stands.
 *
 * Left is the floor (completion, what is released, who is available, pace, zone
 * progress) and right is what needs a person (exceptions). The task counts,
 * activated routes and pickers are live (re-read every 30s). Only the exceptions
 * are sample data, marked as such on their own card - see
 * `-data/dashboard-mock.ts` for what they are waiting on.
 */
export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/dashboard/",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const { lang } = params;
  const { t: typedT } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });
  // The components compose keys, which the typed function rejects.
  const t = typedT as unknown as DashboardT;
  const dashboard = useDashboard();

  return (
    <div className="page-shell">
      <header className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">{t("dashboard.heading")}</h1>
          {dashboard.data && (
            <p className="page-subtitle">
              {t(
                dashboard.data.shift
                  ? "dashboard.subheading"
                  : "dashboard.subheadingNoShift",
                {
                  shift: dashboard.data.shift
                    ? `${dashboard.data.shift.name} · ${formatClock(dashboard.data.shift.startMinute)}–${formatClock(dashboard.data.shift.endMinute)}`
                    : "",
                  asOf: new Intl.DateTimeFormat(lang, {
                    hour: "numeric",
                    minute: "2-digit",
                  }).format(dashboard.data.updatedAt),
                },
              )}
            </p>
          )}
        </div>
      </header>

      {dashboard.isPending && <Loader />}
      {dashboard.isError && (
        <div role="alert">
          <p>{t("dashboard.failed")}</p>
          <p className={styles.note}>{t("dashboard.failureSupport")}</p>
        </div>
      )}
      {dashboard.data && (
        <div className={styles.columns}>
          <div className={styles.column}>
            <CompletionHero
              tasks={dashboard.data.tasks}
              units={dashboard.data.units}
              t={t}
            />
            <StatCards
              params={params}
              tasks={dashboard.data.tasks}
              activatedRoutes={dashboard.data.activatedRoutes}
              notActivated={dashboard.data.notActivated}
              pickers={dashboard.data.pickers}
              pickersDetail={dashboard.data.pickersDetail}
              t={t}
            />
            <PiecesPerHour hourly={dashboard.data.hourly} t={t} />
            <ZoneProgressCard zones={dashboard.data.zones} t={t} />
          </div>
          <div className={styles.column}>
            <ExceptionsPanel
              exceptions={dashboard.data.sample.exceptions}
              t={t}
            />
          </div>
        </div>
      )}
    </div>
  );
}
